import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'Estrutura de aço que aguenta o uso intenso',
    description: 'Cadeiras com base de aço reforçado, assento em espuma D45 e tecido resistente a poeira e umidade. Mobiliário que dura tanto quanto o trabalho do campo.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Mesas amplas para análise de projetos',
    description: 'Tampos de 1,80m a 2,40m para análise de mapas, projetos de crédito rural e laudos técnicos. Espaço de trabalho que acompanha a escala do agronegócio.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do escritório rural',
    description: 'Escritórios de fazenda, cooperativas, consultorias rurais e escritórios de crédito agrícola projetados em 3D antes de fabricar. Você aprova, a gente entrega.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Instalação feita pela nossa equipe, no prazo combinado. Sem terceiros, sem imprevistos. Seu escritório pronto para operar quando você precisar.',
  },
];

const blocks = [
  {
    title: 'O Mobiliário Robusto que o Profissional do Agro Exige',
    text: [
      'O profissional do agronegócio não tem tempo para cadeira que quebra, mesa que balança ou armário que não fecha direito. Nosso mobiliário para o setor rural é projetado para resistir ao uso intenso: cadeiras com base de aço de 5 pontas com rodízios de nylon resistente a piso de concreto e cerâmica, assento em espuma injetada D45 que não afunda com o uso, e tecido de alta durabilidade que resiste a poeira, umidade e uso diário sem descascar.',
      'Para escritórios de fazenda e cooperativas, oferecemos mesas com estrutura metálica tubular de 50x50mm que não vibra durante o trabalho, tampos em MDF de 40mm revestido em HPL resistente a riscos e manchas, e armários de aço com chave para guardar documentos, contratos e laudos técnicos com segurança.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Rural%20agribusiness%20office%20with%20robust%20furniture%2C%20large%20work%20table%20with%20maps%20and%20documents%2C%20sturdy%20chairs%2C%20organized%20professional%20environment%2C%20warm%20natural%20lighting%2C%20agricultural%20consulting%20office%2C%20professional%20photography&width=800&height=500&seq=agro1&orientation=landscape',
    reverse: false,
  },
  {
    title: 'Mesas de Análise para Projetos de Crédito Rural e Laudos Técnicos',
    text: [
      'Engenheiros agrônomos, consultores rurais e gerentes de crédito agrícola trabalham com mapas, plantas, laudos e projetos que exigem espaço de trabalho amplo. Nossas mesas de análise têm tampos de 1,80m a 2,40m de comprimento por 90cm de profundidade, com estrutura que suporta o peso de equipamentos de medição, notebooks e documentação volumosa.',
      'Também oferecemos mesas de reunião para cooperativas e associações rurais com capacidade para 10 a 16 pessoas, armários para arquivo morto com gavetas de aço e chave, e estantes abertas para organização de manuais técnicos, catálogos de insumos e publicações do setor. Tudo com o mesmo padrão de robustez que o agronegócio exige.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Large%20analysis%20table%20in%20agricultural%20consulting%20office%20with%20maps%20and%20technical%20documents%2C%20robust%20furniture%2C%20organized%20professional%20workspace%2C%20warm%20lighting%2C%20agribusiness%20environment&width=800&height=500&seq=agro2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Escritórios de Fazenda, Cooperativas e Consultorias Rurais',
    text: [
      'Atendemos desde o escritório da fazenda — que precisa de mobiliário resistente à poeira e à umidade do campo — até a sede da cooperativa regional, que recebe produtores, técnicos e representantes de bancos e seguradoras. Cada ambiente tem necessidades específicas, e nosso projeto 3D gratuito garante que o mobiliário vai atender a todas elas.',
      'Nosso processo é simples: visita técnica, medição do espaço, projeto 3D em até 48h, aprovação, fabricação e instalação pela nossa equipe. Você não precisa se preocupar com nada — só com a produção. E com suporte local em Caruaru e todo o Agreste Pernambucano, estamos sempre perto quando precisar.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Agricultural%20cooperative%20office%20interior%20with%20organized%20workstations%2C%20robust%20furniture%2C%20meeting%20table%2C%20warm%20professional%20environment%2C%20rural%20business%20setting%2C%20natural%20lighting&width=800&height=500&seq=agro3&orientation=landscape',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: 'https://readdy.ai/api/search-image?query=Robust%20office%20chair%20with%20steel%20base%2C%20durable%20fabric%2C%20high%20density%20foam%2C%20professional%20seating%20for%20agribusiness%2C%20product%20photography%2C%20warm%20studio%20lighting&width=600&height=400&seq=agrog1&orientation=landscape',
    alt: 'Cadeira robusta para escritório rural',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Large%20analysis%20table%20with%20metal%20frame%2C%20HPL%20top%2C%20organized%20workspace%20for%20agricultural%20consulting%2C%20professional%20furniture%2C%20warm%20environment&width=600&height=400&seq=agrog2&orientation=landscape',
    alt: 'Mesa de análise para projetos rurais',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Steel%20filing%20cabinet%20with%20lock%2C%20organized%20document%20storage%2C%20professional%20office%20furniture%20for%20agribusiness%2C%20warm%20environment&width=600&height=400&seq=agrog3&orientation=landscape',
    alt: 'Armário de aço para documentos',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Agricultural%20cooperative%20meeting%20room%20with%20large%20table%2C%20organized%20chairs%2C%20professional%20rural%20business%20environment%2C%20warm%20natural%20lighting&width=600&height=400&seq=agrog4&orientation=landscape',
    alt: 'Sala de reunião para cooperativa',
  },
];

export default function AgronegocioPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário Robusto para Agronegócio e Consultoria Rural | Caruaru PE',
    description: 'Mobiliário robusto para agronegócio: cadeiras de aço, mesas de análise para projetos rurais, armários de aço. Projeto 3D gratuito. Caruaru e Agreste PE.',
    keywords: 'mobiliário agronegócio Recife, escritório fazenda Petrolina, cooperativa rural Garanhuns, mesa análise projetos rurais Arcoverde, móveis consultoria agrícola Sertão PE, armario aço agronegócio Pernambuco, mobiliário rural Agreste, mobiliário agronegócio Nordeste, mesa projeto rural Recife, armário aço fazenda Petrolina, mobiliário cooperativa Garanhuns, mobiliário agronegócio Caruaru, escritório rural Bezerros',
    path: '/agronegocio',
  });

  useSchemaLD({
    id: 'schema-agronegocio',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário Robusto para Agronegócio e Consultoria Rural | Caruaru PE',
      description: 'Mobiliário robusto para agronegócio: cadeiras de aço, mesas de análise para projetos rurais, armários de aço. Projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/agronegocio`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Agronegócio',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20agribusiness%20office%20with%20robust%20furniture%2C%20large%20work%20table%2C%20organized%20workspace%2C%20warm%20natural%20lighting%2C%20agricultural%20consulting%20environment%2C%20professional%20photography%2C%20Caruaru%20Pernambuco&width=1400&height=700&seq=agrohero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Agronegócio · Cooperativas · Consultoria Rural
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              O mobiliário robusto que o profissional do Agro exige.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Cadeiras com base de aço que resistem ao uso intenso, mesas amplas para análise de mapas e projetos de crédito rural, e armários de aço para documentos. Mobiliário que dura tanto quanto o trabalho do campo.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Agroneg%C3%B3cio%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que o agronegócio do Agreste escolhe a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Durabilidade e Resistência</span> para o Campo e o Escritório
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Agroneg%C3%B3cio%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">Projeto 3D do seu escritório rural, antes de gastar um real.</h2>
          <p className="text-gray-400 text-base mb-10">Nossa equipe visita seu escritório, mede o espaço e entrega o projeto 3D em até 48h. Você aprova, a gente fabrica e instala.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Agroneg%C3%B3cio%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Mobiliário para <span className="text-[#E8610A]">Agronegócio</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Agroneg%C3%B3cio%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Agroneg%C3%B3cio%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
